import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Music, Download, Upload, Loader2, CheckCircle, Clock, RotateCw, User, Eye, X, Send, FileAudio,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  pending_upload: { label: 'Esperando archivo', color: 'text-yellow-400 bg-yellow-400/10' },
  uploaded: { label: 'Listo para trabajar', color: 'text-cyan-400 bg-cyan-400/10' },
  in_progress: { label: 'En proceso', color: 'text-orange-400 bg-orange-400/10' },
  revision_requested: { label: 'Revision pedida', color: 'text-purple-400 bg-purple-400/10' },
  delivered: { label: 'Entregado', color: 'text-green-400 bg-green-400/10' },
  completed: { label: 'Completado', color: 'text-green-500 bg-green-500/15' },
};

const MasteringStaffPanel = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(''); // Status filter
  const [detailOrder, setDetailOrder] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadFiles, setUploadFiles] = useState({}); // { orderId: { wav: File, mp3: File } }

  useEffect(() => {
    loadData();
    const t = setInterval(loadData, 15000);
    return () => clearInterval(t);
  }, [filter]);

  const loadData = async () => {
    try {
      const params = filter ? `?status=${filter}` : '';
      const [qRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/api/mastering/staff/queue${params}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/mastering/staff/stats`, { withCredentials: true }),
      ]);
      setOrders(qRes.data);
      setStats(sRes.data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  const downloadSource = async (orderId, songName) => {
    try {
      const res = await axios.get(`${API_URL}/api/mastering/staff/${orderId}/download-source`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `source_${songName.replace(/[^a-z0-9]/gi, '_')}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error descargando');
    }
  };

  const handleFileSelect = (orderId, type, file) => {
    setUploadFiles(prev => ({
      ...prev,
      [orderId]: { ...(prev[orderId] || {}), [type]: file },
    }));
  };

  const uploadMastered = async (orderId) => {
    const files = uploadFiles[orderId] || {};
    if (!files.wav && !files.mp3) {
      alert('Sube al menos el WAV o el MP3 masterizado');
      return;
    }
    setUploadingId(orderId);
    try {
      const fd = new FormData();
      if (files.wav) fd.append('wav_file', files.wav);
      if (files.mp3) fd.append('mp3_file', files.mp3);
      await axios.post(`${API_URL}/api/mastering/staff/${orderId}/upload-mastered`, fd, {
        withCredentials: true, timeout: 300000,
      });
      setUploadFiles(prev => { const n = { ...prev }; delete n[orderId]; return n; });
      await loadData();
      alert('Master entregado. Se notifico al cliente por email.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error subiendo archivos');
    }
    setUploadingId(null);
  };

  if (loading) return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="mastering-staff-panel">
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Music className="w-5 h-5 text-[#E8720C]" /> Canciones de Clientes
        </h2>
        <p className="text-xs text-white/40 mt-1">Masterizaciones pagadas por clientes externos</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Sin subir', value: stats.pending_upload, color: 'text-yellow-400' },
            { label: 'Por trabajar', value: stats.to_work, color: 'text-cyan-400' },
            { label: 'Entregados', value: stats.delivered, color: 'text-green-400' },
            { label: 'Completados', value: stats.completed, color: 'text-green-500' },
            { label: 'Ingresos', value: `$${stats.total_revenue?.toFixed(2) || 0}`, color: 'text-[#E8720C]' },
          ].map((c) => (
            <div key={c.label} className="glass-card p-3">
              <p className="text-[10px] text-white/40 uppercase tracking-wider">{c.label}</p>
              <p className={`text-xl font-bold ${c.color}`}>{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2">
        {[
          { val: '', lbl: 'Activos' },
          { val: 'uploaded', lbl: 'Listos para trabajar' },
          { val: 'in_progress', lbl: 'En proceso' },
          { val: 'revision_requested', lbl: 'Revisiones' },
          { val: 'delivered', lbl: 'Entregados' },
          { val: 'completed', lbl: 'Completados' },
          { val: 'pending_upload', lbl: 'Sin subir' },
        ].map((f) => (
          <button key={f.val} onClick={() => setFilter(f.val)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filter === f.val ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
            data-testid={`mastering-filter-${f.val || 'active'}`}>
            {f.lbl}
          </button>
        ))}
      </div>

      {/* Orders list */}
      {orders.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Music className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-sm text-white/40">No hay canciones {filter ? 'con ese estado' : 'por trabajar'}.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const sc = STATUS_STYLES[o.status] || { label: o.status, color: 'text-white/50 bg-white/5' };
            const files = uploadFiles[o.id] || {};
            return (
              <div key={o.id} className="glass-card p-4" data-testid={`mastering-order-${o.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm">{o.song_name}</p>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${sc.color}`}>{sc.label}</span>
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-[#E8720C]/15 text-[#E8720C]">{o.tier_name}</span>
                      {o.revisions_used > 0 && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-purple-500/15 text-purple-300">
                          Revision #{o.revisions_used}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-white/40 mt-1 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" /> {o.user_name || o.user_email}</span>
                      <span>${o.price}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(o.created_at).toLocaleString()}</span>
                    </p>
                  </div>
                  <button onClick={() => setDetailOrder(o)}
                    className="px-2.5 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 rounded-lg text-xs flex items-center gap-1.5"
                    data-testid={`view-mastering-${o.id}`}>
                    <Eye className="w-3.5 h-3.5" /> Ver
                  </button>
                </div>

                {o.notes && (
                  <p className="text-xs text-white/50 italic mb-3 p-2 bg-white/[0.02] rounded border-l-2 border-[#E8720C]/30">
                    Notas: {o.notes}
                  </p>
                )}
                {o.revision_feedback && o.status === 'revision_requested' && (
                  <p className="text-xs text-purple-300 mb-3 p-2 bg-purple-500/5 rounded border-l-2 border-purple-500/30">
                    <RotateCw className="w-3 h-3 inline mr-1" /> Feedback: {o.revision_feedback}
                  </p>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {o.client_mp3_url && (
                    <button onClick={() => downloadSource(o.id, o.song_name)}
                      className="px-3 py-2 bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                      data-testid={`dl-source-${o.id}`}>
                      <Download className="w-3.5 h-3.5" /> Descargar original
                    </button>
                  )}

                  {(o.status === 'uploaded' || o.status === 'in_progress' || o.status === 'revision_requested') && (
                    <>
                      <label className={`px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 cursor-pointer ${files.wav ? 'bg-green-500/15 text-green-400' : 'bg-white/5 hover:bg-white/10 text-white/70'}`}>
                        <input type="file" accept=".wav" hidden
                          onChange={(e) => handleFileSelect(o.id, 'wav', e.target.files[0])}
                          data-testid={`select-wav-${o.id}`} />
                        <FileAudio className="w-3.5 h-3.5" /> {files.wav ? `WAV: ${files.wav.name.substring(0, 20)}` : 'Seleccionar WAV'}
                      </label>
                      <label className={`px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 cursor-pointer ${files.mp3 ? 'bg-green-500/15 text-green-400' : 'bg-white/5 hover:bg-white/10 text-white/70'}`}>
                        <input type="file" accept=".mp3" hidden
                          onChange={(e) => handleFileSelect(o.id, 'mp3', e.target.files[0])}
                          data-testid={`select-mp3-${o.id}`} />
                        <FileAudio className="w-3.5 h-3.5" /> {files.mp3 ? `MP3: ${files.mp3.name.substring(0, 20)}` : 'Seleccionar MP3'}
                      </label>
                      {(files.wav || files.mp3) && (
                        <button onClick={() => uploadMastered(o.id)}
                          disabled={uploadingId === o.id}
                          className="px-3 py-2 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
                          data-testid={`upload-mastered-${o.id}`}>
                          {uploadingId === o.id ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Subiendo...</> : <><Send className="w-3.5 h-3.5" /> Entregar al cliente</>}
                        </button>
                      )}
                    </>
                  )}

                  {o.status === 'delivered' && (
                    <span className="px-3 py-2 bg-green-500/10 text-green-400 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                      <CheckCircle className="w-3.5 h-3.5" /> Esperando confirmacion cliente
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      {detailOrder && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setDetailOrder(null)}>
          <div className="glass-card p-6 w-full max-w-lg border border-white/10" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">{detailOrder.song_name}</h3>
              <button onClick={() => setDetailOrder(null)}><X className="w-4 h-4 text-white/50" /></button>
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-white/40">Cliente</span><span>{detailOrder.user_name}</span></div>
              <div className="flex justify-between"><span className="text-white/40">Email</span><span>{detailOrder.user_email}</span></div>
              <div className="flex justify-between"><span className="text-white/40">Plan</span><span>{detailOrder.tier_name}</span></div>
              <div className="flex justify-between"><span className="text-white/40">Precio</span><span className="text-[#E8720C] font-bold">${detailOrder.price}</span></div>
              <div className="flex justify-between"><span className="text-white/40">Estado</span><span>{STATUS_STYLES[detailOrder.status]?.label || detailOrder.status}</span></div>
              <div className="flex justify-between"><span className="text-white/40">Creado</span><span>{new Date(detailOrder.created_at).toLocaleString()}</span></div>
              {detailOrder.paid_at && <div className="flex justify-between"><span className="text-white/40">Pagado</span><span>{new Date(detailOrder.paid_at).toLocaleString()}</span></div>}
              {detailOrder.uploaded_at && <div className="flex justify-between"><span className="text-white/40">Archivo subido</span><span>{new Date(detailOrder.uploaded_at).toLocaleString()}</span></div>}
              {detailOrder.delivered_at && <div className="flex justify-between"><span className="text-white/40">Entregado</span><span>{new Date(detailOrder.delivered_at).toLocaleString()}</span></div>}
              {detailOrder.playlist_active && (
                <div className="flex justify-between"><span className="text-white/40">Playlist</span>
                  <span className="text-[#E8720C]">Activa hasta {new Date(detailOrder.playlist_expires_at).toLocaleDateString()}</span></div>
              )}
              {detailOrder.notes && <div className="mt-3 p-2 bg-white/[0.02] rounded"><span className="text-white/40">Notas: </span>{detailOrder.notes}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasteringStaffPanel;
